import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useBootstrapTheme } from "../../../contexts/BootstrapThemeContext";
import ImageProps from "../../../types/imageStudio/images";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan, faX } from "@fortawesome/free-solid-svg-icons";
import { EffectThemeImageGridProps } from "../../../types/imageStudio/imageLists";

const EffectThemeImageGrid = ({
  images,
  size = '110px',
  gap = 10,
  className,
  buttonClassname,
  showPlaceholder = false,
  placeholderContent,
  onImageClick,
  onImageHover,
  onImageHoverEnd,
  onImageSelect,
  onDeleteItem,
  selectedImageIndex = null,
  objectFit='cover',
  backgroundColor='transparent',
  titleDelete,
  titlteImage,
  iconDelete,
  draggableImages = false,
  onImageDragStart,
  onImageContextMenu
}:EffectThemeImageGridProps) => {
  const { t } = useTranslation("imageStudio");
  const { effectiveTheme } = useBootstrapTheme();
  const [hoverItemId, setHoverItemID] = useState<number|null>(null);
  const isDark = effectiveTheme === "dark";

  const borderColor = isDark ? "rgba(255,255,255,0.35)" : "rgba(15,23,42,0.25)";
  const shadow = isDark
    ? "0 10px 26px rgba(96,165,250,0.10)"
    : "0 10px 26px rgba(2,6,23,0.08)";

  return (
    <div
      className={className}
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: `${gap}px`,
        alignItems: "flex-start",
      }}
    >
      {images.length === 0 && showPlaceholder ? (
        <div
          style={{
            width: `${size}`,
            height: `${size}`,
            borderRadius: "0.75rem",
            border: `1px solid ${borderColor}`,
            boxShadow: shadow,
            backdropFilter: "blur(8px)",
            background: backgroundColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0.75,
            fontSize: 12,
            textAlign: "center",
            padding: 8
          }}
        >
          {placeholderContent ?? "No image"}
        </div>
      ) : null}
      {images.map((img, index) => {
        const clickable = !!img.onClick || !!onImageClick || !!onImageSelect;

        return (
          <button
            key={`${img.src}-${index}`}
            type="button"
            title={titlteImage ?? img.title}
            onClick={() => {
              img.onClick?.();
              onImageClick?.(index, img);
              onImageSelect?.(index, img);
            }}
            onMouseEnter={(e) => {
              onImageHover?.(index, img);
              if (!clickable) return;
              (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)";

              if(onDeleteItem){
                setHoverItemID(index);
              }
            }}
            onMouseLeave={(e) => {
              onImageHoverEnd?.(index, img);
              (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)";
              if(onDeleteItem){
                setHoverItemID(null);
              }
            }}
            draggable={draggableImages}
            onDragStart={(event) => {
                if (!draggableImages) return;

                event.dataTransfer.effectAllowed = "copy";
                onImageDragStart?.(event, index, img);
            }}
            onContextMenu={(event) => {
                if (!onImageContextMenu) return;

                event.preventDefault();
                event.stopPropagation();

                onImageContextMenu(event, index, img);
            }}
            disabled={!clickable}
            className={buttonClassname}
            style={{
              all: "unset",
              cursor: clickable ? "pointer" : "default",
              width: `${size}`,
              height: `${size}`,
              borderRadius: "0.75rem",
              border: index === selectedImageIndex
                ? "2px solid #9381FF"
                : `1px solid ${borderColor}`,
              overflow: "hidden",
              boxShadow: shadow,
              backdropFilter: "blur(8px)",
              display: "block",
              position: "relative",
              opacity: clickable ? 1 : 0.98,
              background: backgroundColor
            }}
          >
            <img
              src={img.src}
              alt={img.alt ?? `image-${index}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: objectFit,
                display: "block",
              }}
              draggable={draggableImages}
              onDragStart={(event) => {
                  if (!draggableImages) return;

                  event.dataTransfer.effectAllowed = "copy";
                  onImageDragStart?.(event, index, img);
              }}
            />
            {
              index === hoverItemId
              ? <>
                  <div className="position-absolute w-100 h-100"
                    style={{
                          backgroundColor: "rgba(0,0,0,0.3)",
                          top: 0,
                          pointerEvents: "none"
                      }}
                  >
                    <button
                        className="position-absolute btn border-0 bg-transparent m-0 p-0"
                        style={{
                            top: 0,
                            right: 5,
                            zIndex: 2000,
                            pointerEvents: "auto"
                        }}
                        onClick={() => onDeleteItem?.(index)}
                        title={titleDelete ?? t("page.deleteItem")}
                      >
                        <FontAwesomeIcon size="sm" icon={iconDelete ?? faTrashCan}
                        />
                      </button>
                  </div>
                  <div className="position-absolute w-100"
                    style={{
                        backgroundColor: isDark ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.4)",
                        top: 0,
                        height: 25,
                        pointerEvents: "none"
                    }}
                />
                </>
              : <></>
            }
            
          </button>
        );
      })}
    </div>
  );
};

export default EffectThemeImageGrid;
